import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  Box,
  CircularProgress,
  TextField
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import api, { ProductDto } from "../../../api";
import { arrayNotEmpty, notif } from "../../utils/util";

export type ProductAutocompleteProps = {
  productId?: string;
  onChange?: (productId: string) => void;
};

type LoadState = {
  loaded: boolean;
  productId?: string;
  sku?: string;
};

export const ProductAutocomplete = (props: ProductAutocompleteProps) => {
  const { productId, onChange } = props;

  const [loadState, setLoadState] = useState<LoadState>({ loaded: false });
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<ProductDto[]>([]);
  const [sku, setSKU] = useState("");
  const [selected, setSelected] = useState<ProductDto | null>(null);
  const loading = open && items.length === 0;

  const productLoaded = (): boolean => {
    if (arrayNotEmpty(items) && productId) {
      for (const item of items) {
        if (item.id === productId) {
          return true;
        }
      }
    }
    return false;
  };

  const load = useCallback(async () => {
    try {
      const loadSingle = async () => {
        try {
          const product = await api.product.get(productId!);
          setItems([product]);
          setSelected(product);
        } catch {
          setItems([]);
        }
      };
      const loadPage = async () => {
        try {
          const result = await api.product.find({ page: 1, size: 20, sku } as any);
          setItems(result.data);
        } catch {
          setItems([]);
        }
      };

      if (!loadState.loaded) {
        if (productId) {
          await loadSingle();
        } else {
          await loadPage();
        }
      } else if (loadState.sku !== sku) {
        await loadPage();
      } else if (loadState.productId !== productId && !productLoaded()) {
        await loadSingle();
      }
      setLoadState({ loaded: true, productId, sku });
    } catch (err: any) {
      notif(err, "error");
    }
  }, [productId, sku]);

  const handleInputChange = (event: React.SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => {
    // console.log(`input change: ${value}`);
    setSKU(value);
  };

  const handleChange = (event: React.SyntheticEvent, value: ProductDto | null, reason: AutocompleteChangeReason, details?: AutocompleteChangeDetails<ProductDto>) => {
    // console.log(`value change: ${JSON.stringify(value)}`);
    if (value && value.id) {
      setSelected(value);
      if (onChange) onChange(value.id);
    }
  };

  useEffect(() => {
    load();
  }, [productId, sku]);

  return (
    <Autocomplete
      id="product-list"
      // sx={{ width: 300 }}
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      onInputChange={handleInputChange}
      onChange={handleChange}
      isOptionEqualToValue={(option, value) => option.sku === value.sku}
      getOptionLabel={(option) => option.sku || ""}
      options={items}
      value={selected}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Products"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <Box>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </Box>
            ),
          }}
        />
      )}
    />
  );
};
